import {DataTableColumn} from 'mantine-datatable';
import {I18n} from 'aws-amplify/utils';
import {NavLink} from 'react-router-dom';
import {Text} from '@mantine/core';

import {formatTimestamp} from '../../../utils/TableTimestamps';
import {TopLevelDomain} from '../types';
import {useVerifiedPermissions} from '@flexinet/auth-components';

// FNP-1292 — THE COLUMNS, as `DataTableColumn<TopLevelDomain>[]`.
//
// ⚠️ EVERY COLUMN CARRIES AN `accessor`, and that is the whole reason this file
// replaced `tables/columns.tsx`. The old config was `{id, title, render}`:
// `render` says how a cell LOOKS, `accessor` says what sorting sorts BY. A
// render-only config reads as complete and makes sorting impossible without
// touching every column, which is why the checklist puts it in the root-cause
// box rather than under sorting.
//
// ⚠️ NO COLUMN DECLARES A WIDTH. FNP-1284: the fixed pixel widths caused the
// phantom horizontal scrollbar, because pinned columns commit their pixels
// before the flexible one gets anything, so the flexible column content-sizes
// past the container. `minWidth` in px is valid if a floor is ever wanted; a
// grid fraction is not. See `src/features/zones/tests/README-column-widths.md`.
export const useTopLevelDomainsTableColumns = () => {

    const {allowedActions} = useVerifiedPermissions();

    const columns: DataTableColumn<TopLevelDomain>[] = [
        {
            accessor: 'name',
            sortable: true,
            title: I18n.get('Name'),
            // The row's front door — no width, so it claims the slack.
            //
            // ⚠️ The link is gated on Edit, matching the old table: a caller
            // who cannot edit gets plain text rather than a link into a page
            // they would be refused at.
            render: (item: TopLevelDomain) => (
                allowedActions.includes('EditTopLevelDomain')
                    ? <NavLink to={`/top-level-domains/${item.id}`}>{item.name ?? '—'}</NavLink>
                    : <Text size={'sm'}>{item.name ?? '—'}</Text>
            ),
        },
        {
            accessor: 'tldType',
            sortable: true,
            title: I18n.get('TLD Type'),
            render: (item: TopLevelDomain) => (
                <Text size={'sm'}>{item.tldType ?? '—'}</Text>
            ),
        },
        {
            accessor: 'tldManager',
            sortable: true,
            title: I18n.get('Manager'),
            render: (item: TopLevelDomain) => (
                <Text size={'sm'}>{item.tldManager ?? '—'}</Text>
            ),
        },
        {
            accessor: 'created',
            sortable: true,
            title: I18n.get('Created'),
            render: (item: TopLevelDomain) => formatTimestamp(item.created),
        },
    ];

    return {columns};
};
